// src/pages/AdminConsole.tsx
import { useQueryClient } from '@tanstack/react-query'
import {
  Download, Gift, GaugeCircle, Grid3x3, LogOut, MapPinned, MessageSquareHeart,
  ScrollText, SlidersHorizontal, Trophy, Users,
} from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts'
import { PortalFooter, PortalHeader } from '../components/PortalChrome'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../components/ui/dialog'
import { cn } from '../lib/cn'
import { post } from '../lib/api'
import { clearStaffSession, getStaffProfile, getStaffSession } from '../lib/staff'
import { useAdminStream } from '../lib/useAdminStream'
import { AButton } from './admin/Section'
import type { AdminDashboard } from './admin/types'
import { DashboardSection } from './admin/DashboardSection'
import { JudgingSection } from './admin/JudgingSection'
import { ResultsSection } from './admin/ResultsSection'
import { StudentsSection } from './admin/StudentsSection'
import { AccountsSection } from './admin/AccountsSection'
import { CeremonySection } from './admin/CeremonySection'
import { ExportsSection } from './admin/ExportsSection'
import { BoothsSection } from './admin/BoothsSection'
import { FeedbackSection } from './admin/FeedbackSection'
import { SettingsSection } from './admin/SettingsSection'

const SECTIONS = [
  { key: 'dashboard', label: 'Dashboard', icon: GaugeCircle },
  { key: 'judging', label: 'Judging', icon: Grid3x3 },
  { key: 'results', label: 'Results', icon: Trophy },
  { key: 'students', label: 'Students', icon: Users },
  { key: 'booths', label: 'Booth map', icon: MapPinned },
  { key: 'accounts', label: 'Accounts', icon: ScrollText },
  { key: 'ceremony', label: 'Big screen', icon: Gift },
  { key: 'feedback', label: 'Feedback', icon: MessageSquareHeart },
  { key: 'exports', label: 'Exports', icon: Download },
  /* Last on purpose: the one section that changes how the app behaves for
     everyone, so it is not a neighbour of anything used in a hurry. */
  { key: 'settings', label: 'Settings', icon: SlidersHorizontal },
] as const

type SectionKey = (typeof SECTIONS)[number]['key']

const DEFAULT_SECTION: SectionKey = 'dashboard'

const isSectionKey = (value: string | null): value is SectionKey =>
  SECTIONS.some((s) => s.key === value)

/** Small donut: done vs remaining. */
function Donut({ done, total, color }: { done: number; total: number; color: string }) {
  const data = [
    { name: 'done', v: done },
    { name: 'left', v: Math.max(total - done, 0) },
  ]
  return (
    <div className="relative size-20 shrink-0">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={data} dataKey="v" innerRadius={26} outerRadius={38} startAngle={90} endAngle={-270} stroke="none" isAnimationActive={false}>
            <Cell fill={color} />
            <Cell fill="#E2E8F0" />
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <span className="absolute inset-0 grid place-items-center text-sm font-extrabold tabular-nums text-[#0B1E3F]">
        {total ? Math.round((done / total) * 100) : 0}%
      </span>
    </div>
  )
}

function Stat({ label, value, sub, children }: { label: string; value: string; sub?: string; children?: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-white bg-white/95 p-3.5 shadow-lg shadow-blue-900/5">
      {children}
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">{label}</p>
        <p className="text-2xl font-black leading-tight tabular-nums text-[#0B1E3F]">{value}</p>
        {sub && <p className="text-xs text-slate-500">{sub}</p>}
      </div>
    </div>
  )
}

export default function AdminConsole() {
  const navigate = useNavigate()
  const signedIn = !!getStaffSession()
  const profile = getStaffProfile()
  const [isConfirmingSignOut, setIsConfirmingSignOut] = useState(false)

  /*
   * Which section is open lives in the URL — `/admin?section=settings`.
   *
   * It was `useState('dashboard')`, which meant every reload threw the
   * Committee back to the Dashboard. That is worst exactly where it was most
   * likely to happen: Settings and Big screen are the two sections someone
   * refreshes while watching for a change to land, and both are several
   * clicks from the Dashboard.
   *
   * The URL rather than localStorage, though localStorage would also survive
   * a reload. Three reasons:
   *
   *   - **The address stays honest.** Two laptops are signed in as Admin on
   *     event day; "open /admin?section=ceremony" is a thing one person can
   *     say to the other, and a bookmark lands where it says it does.
   *   - **One source of truth.** Stored state and the URL can disagree, and
   *     then the page you are looking at depends on which one some effect
   *     read last.
   *   - **It is per tab.** The Committee runs the ceremony controls and the
   *     register side by side; a shared stored value would have had the two
   *     tabs fighting over which section was "current".
   *
   * An unknown or absent `section` falls back to the Dashboard rather than
   * rendering nothing, so a mistyped URL is merely wrong, not broken.
   */
  const [searchParams, setSearchParams] = useSearchParams()
  const requested = searchParams.get('section')
  const section: SectionKey = isSectionKey(requested) ? requested : DEFAULT_SECTION

  const setSection = useCallback(
    (key: SectionKey) => {
      /*
       * `replace`, not push: this is one workspace, not nine pages. Pushing
       * would mean a Committee member who has clicked through eight sections
       * has to press Back eight times to leave the console — and Back is
       * what someone reaches for when they want *out* of a screen, not when
       * they want the previous tab.
       *
       * Other query parameters are preserved rather than overwritten, so
       * anything a section puts in the URL later still survives a tab change.
       */
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous)
          next.set('section', key)
          return next
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  const live = useAdminStream<AdminDashboard>(signedIn)
  const overview = live.data?.overview

  /*
   * The big-screen controls refresh off this stream, not off their own timer.
   *
   * They used to poll, and TanStack Query stops polling the moment the window
   * loses focus. On event day the console sits *behind* the projector window
   * — we tell the Committee to drag the screen to the second display — so the
   * console went stale exactly when it mattered: a winner was drawn, the new
   * row never appeared, and "Mark absent" and "Draw replacement" were not
   * there to click. A background tab throttles timers to about one a minute,
   * which is what made it look intermittent.
   *
   * The stream is already open for the dashboard and is not throttled the way
   * timers are, so every change lands immediately whether the window is in
   * front or not.
   */
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!live.data) return
    for (const key of [['admin-draws'], ['admin-ceremony'], ['admin-screen-focus'], ['admin-attire']]) {
      queryClient.invalidateQueries({ queryKey: key })
    }
  }, [live.data, queryClient])

  useEffect(() => {
    if (!signedIn) navigate('/staff/login', { replace: true })
  }, [signedIn, navigate])

  async function signOut() {
    await post('/api/staff/logout', {}, 'staff').catch(() => { })
    clearStaffSession()
    navigate('/staff/login', { replace: true })
  }

  const badge = (key: string) =>
    key === 'judging' && overview
      ? `${overview.judging.submitted}/${overview.judging.total}`
      : key === 'students' && overview
        ? String(overview.students.registered)
        : null

  return (
    <div className="admin-console flex h-dvh select-none flex-col overflow-hidden bg-gradient-to-br from-[#BFD8FA] via-[#E9F1FD] to-[#F5EFFC] font-sans text-[#0B1E3F] [-webkit-tap-highlight-color:transparent] [&_input]:select-text [&_td]:select-text">
      <PortalHeader
        portal="Committee portal"
        identity={profile?.name ?? 'Committee admin'}
        details={`Administrator${profile?.username ? ` · ${profile.username}` : ''}`}
        onSignOut={() => setIsConfirmingSignOut(true)}
      />

      {/*
        Padded on both sides. It was `pl-3 pr-0` so the tab strip could bleed
        off the right edge as a scroll affordance — but that stripped the
        right margin from every panel too, so cards and tables ran flush into
        the screen edge on a phone. The tabs get their own negative margin
        below instead, which keeps the bleed without costing the content.
      */}
      <div className="flex min-h-0 w-full flex-1 flex-col gap-3 px-3 py-3 sm:px-5 sm:py-5 lg:flex-row">
        {/* Menu: scrolling tabs on phone/tablet, sidebar on laptop */}
        <nav aria-label="Admin sections" className="shrink-0 lg:w-56">
          <div className="-mx-3 flex gap-2 overflow-x-auto px-3 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:-mx-5 sm:px-5 lg:mx-0 lg:flex-col lg:overflow-visible lg:rounded-2xl lg:border lg:border-white lg:bg-white/90 lg:p-2 lg:shadow-lg lg:shadow-blue-900/5">
            {SECTIONS.map((s) => {
              const Icon = s.icon
              const active = s.key === section
              const b = badge(s.key)
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setSection(s.key)}
                  className={cn(
                    'flex shrink-0 items-center gap-2 rounded-xl border px-3.5 py-2 text-[13px] font-semibold transition-colors lg:w-full lg:border-transparent',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/40',
                    active
                      ? 'border-[#1B2A6B] bg-[#1B2A6B] text-white shadow-md shadow-blue-900/20'
                      : 'border-white bg-white/80 text-slate-600 hover:bg-white lg:bg-transparent lg:hover:bg-slate-50',
                  )}
                >
                  <Icon className={cn('size-4', active ? 'text-amber-400' : 'text-slate-400')} />
                  {s.label}
                  {b && (
                    <span className={cn('ml-auto rounded-full px-2 py-0.5 text-[10.5px] font-bold tabular-nums', active ? 'bg-white/15 text-white' : 'bg-slate-100 text-slate-500')}>
                      {b}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </nav>

        {/* Content */}
        <main className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
          {section === 'dashboard' && overview && (
            <div className="grid shrink-0 gap-3 sm:grid-cols-2">
              <Stat label="Judging submitted" value={`${overview.judging.submitted}/${overview.judging.total}`} sub="score sheets locked">
                <Donut done={overview.judging.submitted} total={overview.judging.total} color="#10B981" />
              </Stat>
              <Stat label="Students registered" value={String(overview.students.registered)} sub="on the register" />
            </div>
          )}

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            {section === 'dashboard' && <DashboardSection live={live} />}
            {section === 'judging' && <JudgingSection live={live} />}
            {section === 'results' && <ResultsSection />}
            {section === 'students' && <StudentsSection />}
            {section === 'booths' && <BoothsSection />}
            {section === 'accounts' && <AccountsSection />}
            {section === 'ceremony' && <CeremonySection />}
            {section === 'feedback' && <FeedbackSection />}
            {section === 'exports' && <ExportsSection />}
            {section === 'settings' && <SettingsSection />}
          </div>
        </main>
      </div>

      <PortalFooter />

      <Dialog open={isConfirmingSignOut} onOpenChange={setIsConfirmingSignOut}>
        <DialogContent className="p-0" showClose={false}>
          <div className="px-6 pb-6 pt-7 text-center">
            <span className="mx-auto mb-3 grid size-12 place-items-center rounded-full bg-[#1B2A6B] text-white">
              <LogOut className="size-5" strokeWidth={2.2} />
            </span>
            <DialogTitle className="text-lg font-bold text-[#0B1E3F]">Sign out?</DialogTitle>
            <DialogDescription className="mt-1.5 text-sm text-slate-500">
              You will need your username and password to sign back in.
            </DialogDescription>
            <div className="mt-6 flex gap-2.5">
              <AButton
                tone="outline"
                block
                autoFocus
                onClick={() => setIsConfirmingSignOut(false)}
              >
                Stay signed in
              </AButton>
              <AButton tone="navy" block onClick={signOut}>
                Sign out
              </AButton>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
